import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Card from '../../ui/Card';
import Button from '../../ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import './RegisterForm.css';

/**
 * Registration form component for user account creation
 */
const RegisterForm = () => {
  const [email, setEmail] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, error: authError, clearError } = useAuth();
  const navigate = useNavigate();
  
  // Clear any auth context errors when form mounts
  useEffect(() => {
    clearError();
  }, [clearError]);
  
  const validateForm = () => {
    const errors = {};
    let isValid = true;
    
    // Email validation
    if (!email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email address is invalid';
      isValid = false;
    }
    
    // Password validation
    if (!password1) {
      errors.password1 = 'Password is required';
      isValid = false;
    } else if (password1.length < 8) {
      errors.password1 = 'Password must be at least 8 characters';
      isValid = false;
    }
    
    // Password confirmation
    if (!password2) {
      errors.password2 = 'Please confirm your password';
      isValid = false;
    } else if (password1 !== password2) {
      errors.password2 = 'Passwords do not match';
      isValid = false;
    }
    
    setFormErrors(errors);
    return isValid;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset errors
    setFormErrors({});
    setGeneralError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsSubmitting(true);
      await register(email, password1, password2);
      
      // Success message and form reset
      setSuccessMessage(
        'Registration successful! Please check your email for a verification link.'
      );
      setEmail('');
      setPassword1('');
      setPassword2('');
      
      // Navigate to verification page after short delay
      setTimeout(() => {
        navigate('/email/verification/');
      }, 3000);
    } catch (error) {
      // Only log errors in non-test environments 
      if (process.env.NODE_ENV !== 'test') {
        console.error('Registration failed:', error);
      }
      
      // Only log in non-production environments
      if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
        console.log('Registration error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          email
        });
      }
      
      // Handle 409 Conflict specifically (email already exists)
      if (error.response?.status === 409) {
        setFormErrors({
          email: 'This email address is already registered. Please use a different email or sign in instead.'
        });
        return;
      }
      
      // Handle different error formats from django-allauth headless API
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        
        // Handle field-specific errors
        const fieldErrors = {};
        let hasFieldErrors = false;
        
        // Check for specific field errors based on the param field
        for (const errorItem of errors) {
          const param = errorItem.param;
          const message = errorItem.message;
          
          if (param && ['email', 'password1', 'password2'].includes(param)) {
            fieldErrors[param] = message;
            hasFieldErrors = true;
          } else if (!param || param === 'non_field_errors' || param === '__all__') {
            // This is a general error
            setGeneralError(message);
          }
        }
        
        if (hasFieldErrors) {
          setFormErrors(fieldErrors);
        }
      } else if (authError) {
        // Use error from auth context if available
        setGeneralError(authError);
      } else {
        setGeneralError('Registration failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Card className="register-form">
      <h2 className="register-form__title">Create an Account</h2>
      
      {/* Success message */}
      {successMessage && (
        <div className="register-form__message register-form__message--success">
          {successMessage}
        </div>
      )}
      
      {/* Error message */}
      {(generalError || authError) && (
        <div className="register-form__message register-form__message--error">
          {generalError || authError}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="register-form__field">
          <label htmlFor="email" className="register-form__label">
            Email
          </label>
          <input
            id="email"
            type="email"
            className="register-form__input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            disabled={isSubmitting}
            required
          />
          {formErrors.email && (
            <div className="register-form__field-error">{formErrors.email}</div>
          )}
        </div>
        
        <div className="register-form__field">
          <label htmlFor="password1" className="register-form__label">
            Password
          </label>
          <input
            id="password1"
            type="password"
            className="register-form__input"
            value={password1}
            onChange={(e) => setPassword1(e.target.value)}
            placeholder="Password"
            disabled={isSubmitting}
            required
          />
          {formErrors.password1 && (
            <div className="register-form__field-error">{formErrors.password1}</div>
          )}
        </div>
        
        <div className="register-form__field">
          <label htmlFor="password2" className="register-form__label">
            Confirm Password
          </label>
          <input
            id="password2"
            type="password"
            className="register-form__input"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            placeholder="Confirm password"
            disabled={isSubmitting}
            required
          />
          {formErrors.password2 && (
            <div className="register-form__field-error">{formErrors.password2}</div>
          )}
        </div>
        
        <div className="register-form__footer">
          <div className="register-form__actions">
            <Button 
              type="submit" 
              variant="primary" 
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>
          </div>
          
          <div>
            <span>Already have an account? </span>
            <Link to="/accounts/login/" className="register-form__login-link">
              Sign In
            </Link>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default RegisterForm;